import React from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { defineNextPage } from '@/common/next';
import FeatureContext from '@/pages/--roboneo/landing_page/index';
import SeoFooter from '@/components/SeoFooter';
import SeoCopyRight from '@/components/SeoCopyRight';
import { useLanguageStore } from '$/store/languageStore';
import styles from './index.module.scss';

const V0SeedanceCom = dynamic(() => import('@/pages/--roboneo/landing_page/components/seedance_page/index'));

/** 标题和描述跟随当前语言；语言包加载前使用页面数据中的英文文案，保证首屏元信息非空。 */
function SeedanceHead({ seoTitle, seoDescription }: { seoTitle: string; seoDescription: string; }) {
  const translate = useLanguageStore((state) => state.languageStore.smartcatTranslationText);
  return (
    <Head>
      <title>{translate((value) => value.d437d174a2b813cbd7a605a9f8625279) || seoTitle}</title>
      <meta
        name="description"
        content={translate((value) => value.e3217e139e3959b4438de9df939da43a) || seoDescription}
      />
      {/* Seedance 默认使用英文入口，默认语言与英文指向同一页面。 */}
      <link key="alternate-en" rel="alternate" hrefLang="en" href="https://www.roboneo.com/seedance" />
      <link key="alternate-default" rel="alternate" hrefLang="x-default" href="https://www.roboneo.com/seedance" />
    </Head>
  );
}

/**
 * 检测 User-Agent 是否来自美图系 App 内嵌 WebView（与 SubApp 热区等逻辑一致：美图 App、开拍等）。
 */
function isMeituFamilyAppWebViewUserAgent(userAgent?: string): boolean {
  if (!userAgent) return false;
  return /com\.meitu|com\.starii\.xdesign/i.test(userAgent);
}

/**
 * 解析端上内嵌标记，并按默认英文入口读取已有 Smartcat 标题和描述，供服务端首屏使用。
 */
async function getSeedancePageInitialProps(context: { asPath?: string; }) {
  const asPath = context.asPath || '';
  const search = asPath.includes('?') ? asPath.split('?')[1] ?? '' : '';
  const searchParams = new URLSearchParams(search);
  const inApp = searchParams.get('in_app') === 'true';
  const locale = await import('$/store/languageStore/smartcatTranslation/en.json');
  // 只下发页面元信息，避免序列化整份语言包，也不修改服务端共享的语言 Store。
  return {
    inApp,
    seoTitle: locale.default.d437d174a2b813cbd7a605a9f8625279,
    seoDescription: locale.default.e3217e139e3959b4438de9df939da43a,
  };
}

/**
 * 独立 Seedance 落地页：不依赖 Essio CMS visitor-api。
 * 与 [...path].page.tsx 中 v0Seedance 分支一致，避免本地/未发布 CMS 时出现 NotFound。
 */
export default defineNextPage<{ inApp: boolean; userAgent?: string; seoTitle: string; seoDescription: string; }>({
  pageConfig: {
    projectId: '/--roboneo',
    layoutId: '',
    /** 子应用加载前先输出 SEO 元信息，保证服务端首屏可被抓取。 */
    renderHead({ pageProps }) {
      return <SeedanceHead seoTitle={pageProps.seoTitle} seoDescription={pageProps.seoDescription} />;
    },
  },
  getInitialProps: getSeedancePageInitialProps,
  /** 保留 Seedance 元信息，并将全局导航放在正文外，避免被识别为 main 内容。 */
  component: ({
    inApp, userAgent, seoTitle, seoDescription,
  }) => {
    const ua = userAgent ?? (typeof window !== 'undefined' ? window.navigator.userAgent : undefined);
    const hideLandingChrome = inApp || isMeituFamilyAppWebViewUserAgent(ua);
    return (
      <>
        <SeedanceHead seoTitle={seoTitle} seoDescription={seoDescription} />
        <FeatureContext
          v0Thumail
          hideHeader={hideLandingChrome}
          landingHeaderShowUserCardSystemSetting={!hideLandingChrome}
          // 导航与 main 同级；固定导航占位保留在正文中，维持首屏位置和高度。
          renderLayout={({ header, placeholder, content }) => (
            <>
              {header}
              <main className={styles.main}>
                {placeholder}
                {content}
              </main>
            </>
          )}
        >
          <V0SeedanceCom />
        </FeatureContext>
        <footer className={styles.footerWrap}>
          <SeoFooter />
          <SeoCopyRight />
        </footer>
      </>
    );
  },
});
